import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { isInstructionalVideosRequired } from "../../utils";
import {
  WriteQuestionnaire,
  DISABILITY_PENSION_TYPE,
} from "@/types/questionnaire";
import DatePicker from "@/components/DatePicker";
import Field from "@/components/Field";
import { Input } from "@/components/ui/input";
import { QuestionnaireFormSection } from "../shared/QuestionnaireFormSection";
import { QuestionnaireCheckboxField } from "../shared/QuestionnaireCheckboxField";
import { QuestionnaireConditionalSection } from "../shared/QuestionnaireConditionalSection";
import DisabilityPensionTypeSelect from "@/components/DisabilityPensionTypeSelect";
import { QuestionnaireExpiredDateWarningField } from "../shared/QuestionnaireExpiredDateWarningField";
import { QuestionnaireWarningAlert } from "../shared/QuestionnaireWarningAlert";
import { QUESTIONNAIRE_INSTRUCTIONAL_VIDEO_URL } from "@/constants";
import { ReadQuestionnaire } from "@/types/questionnaire";
import { QuestionnaireAttachmentMeta } from "../../hooks/attachment";
import { QuestionnaireAttachmentField } from "../shared/QuestionnaireAttachmentField";
import { getEmploymentCertificateWarningTranslationKey } from "./utils/getEmploymentCertificateWarningTranslationKey";

interface QuestionnaireStatementsSectionProps {
  questionnaire: ReadQuestionnaire["questionnaire"];
  attachments: Record<string, QuestionnaireAttachmentMeta[]>;
  disabled?: boolean;
}

export function QuestionnaireStatementsSection({
  questionnaire,
  attachments,
  disabled = false,
}: QuestionnaireStatementsSectionProps) {
  const { t } = useTranslation();
  const form = useFormContext<WriteQuestionnaire>();

  const isStudent = form.watch("is_student");
  const isEmployed = form.watch("is_employed");
  const birthDate = form.watch("birth_date");
  const showVoluntarySicknessInsurance = !isStudent && !isEmployed;
  const hasWatchedInstructionalVideos =
    questionnaire.watched_instructional_videos;
  const disabilityPensionType = form.watch("disability_pension_type");
  const isAmendData = questionnaire.agreement_status === "agreement_amend_data";
  const isStudentDisabled = disabled || isAmendData;
  const isEmploymentDisabled = disabled || isAmendData;
  const isVoluntarySicknessInsuranceDisabled = disabled || isAmendData;
  const employmentCertificateIssueDate = form.watch(
    "employment_certificate_issue_date",
  );
  const employmentCertificateAttachments = form.watch(
    "employment_certificate_attachments",
  );
  const employmentCertificateWarningTranslationKey =
    getEmploymentCertificateWarningTranslationKey({
      isEmployed,
      issueDate: employmentCertificateIssueDate,
      existingAttachments: attachments.employment_certificate_attachments,
      newAttachments: employmentCertificateAttachments,
    });

  useEffect(() => {
    if (!showVoluntarySicknessInsurance) {
      form.setValue("voluntary_sickness_insurance", false);
    }
  }, [form, showVoluntarySicknessInsurance]);

  return (
    <QuestionnaireFormSection
      title={t("questionnaires.form.statements.title")}
      className="md:col-span-2"
    >
      <div className="space-y-4">
        <QuestionnaireCheckboxField
          name="is_student"
          label={t("questionnaires.form.statements.student")}
          disabled={isStudentDisabled}
        />

        <QuestionnaireConditionalSection condition={form.watch("is_student")}>
          <Field
            name="school_name"
            label={t("questionnaires.form.conditionalFields.school_name")}
            render={({ field }) => (
              <Input
                {...field}
                disabled={isStudentDisabled}
                placeholder={t(
                  "questionnaires.form.conditionalFields.school_name",
                )}
              />
            )}
          />
          <Field
            name="student_id_number"
            label={t("questionnaires.form.conditionalFields.student_id_number")}
            render={({ field }) => (
              <Input
                {...field}
                disabled={isStudentDisabled}
                placeholder={t(
                  "questionnaires.form.conditionalFields.student_id_number",
                )}
              />
            )}
          />
          <QuestionnaireExpiredDateWarningField
            form={form}
            name="student_id_valid_until"
            label={t(
              "questionnaires.form.conditionalFields.student_id_valid_until",
            )}
            render={({ field }) => (
              <DatePicker
                className="w-full"
                {...field}
                disabled={isStudentDisabled}
              />
            )}
          />
          <QuestionnaireAttachmentField
            name="student_id_attachments"
            label={t(
              "questionnaires.form.conditionalFields.student_id_attachments",
            )}
            existingAttachments={attachments.student_id_attachments}
            disabled={isStudentDisabled}
          />
        </QuestionnaireConditionalSection>
      </div>

      <div className="space-y-4">
        <QuestionnaireCheckboxField
          name="is_employed"
          label={t("questionnaires.form.statements.employment")}
          disabled={isEmploymentDisabled}
        />

        <QuestionnaireConditionalSection condition={form.watch("is_employed")}>
          <Field
            name="employer_name"
            label={t("questionnaires.form.conditionalFields.employer_name")}
            render={({ field }) => (
              <Input
                {...field}
                disabled={isEmploymentDisabled}
                placeholder={t(
                  "questionnaires.form.conditionalFields.employer_name",
                )}
              />
            )}
          />
          <Field
            name="employment_certificate_issue_date"
            label={t(
              "questionnaires.form.conditionalFields.employment_certificate_issue_date",
            )}
            render={({ field }) => (
              <DatePicker
                className="w-full"
                {...field}
                disabled={isEmploymentDisabled}
              />
            )}
          />
          <QuestionnaireAttachmentField
            name="employment_certificate_attachments"
            label={t(
              "questionnaires.form.conditionalFields.employment_certificate_attachments",
            )}
            existingAttachments={attachments.employment_certificate_attachments}
            disabled={isEmploymentDisabled}
          />
          {employmentCertificateWarningTranslationKey && (
            <QuestionnaireWarningAlert
              message={t(employmentCertificateWarningTranslationKey)}
            />
          )}
        </QuestionnaireConditionalSection>
      </div>

      {showVoluntarySicknessInsurance && (
        <QuestionnaireCheckboxField
          name="voluntary_sickness_insurance"
          label={t(
            "questionnaires.form.statements.voluntary_sickness_insurance",
          )}
          disabled={isVoluntarySicknessInsuranceDisabled}
        />
      )}

      <div className="space-y-4">
        <QuestionnaireCheckboxField
          name="is_pensioner"
          label={t("questionnaires.form.statements.pensioner")}
          disabled={disabled}
        />

        <QuestionnaireConditionalSection condition={form.watch("is_pensioner")}>
          <Field
            name="pension_number"
            label={t("questionnaires.form.conditionalFields.pension_number")}
            render={({ field }) => (
              <Input
                {...field}
                disabled={disabled}
                placeholder={t(
                  "questionnaires.form.conditionalFields.pension_number",
                )}
              />
            )}
          />
          <Field
            name="pension_start_date"
            label={t(
              "questionnaires.form.conditionalFields.pension_start_date",
            )}
            render={({ field }) => (
              <DatePicker className="w-full" {...field} disabled={disabled} />
            )}
          />
          <Field
            name="pension_office_name"
            label={t(
              "questionnaires.form.conditionalFields.pension_office_name",
            )}
            render={({ field }) => (
              <Input
                {...field}
                disabled={disabled}
                placeholder={t(
                  "questionnaires.form.conditionalFields.pension_office_name",
                )}
              />
            )}
          />
        </QuestionnaireConditionalSection>
      </div>

      <div className="space-y-4">
        <QuestionnaireCheckboxField
          name="is_disability_pensioner"
          label={t("questionnaires.form.statements.disability_pensioner")}
          disabled={disabled}
        />

        <QuestionnaireConditionalSection
          condition={form.watch("is_disability_pensioner")}
        >
          <Field
            name="disability_pension_number"
            label={t(
              "questionnaires.form.conditionalFields.disability_pension_number",
            )}
            render={({ field }) => (
              <Input
                {...field}
                disabled={disabled}
                placeholder={t(
                  "questionnaires.form.conditionalFields.disability_pension_number",
                )}
              />
            )}
          />
          <Field
            name="disability_pension_start_date"
            label={t(
              "questionnaires.form.conditionalFields.disability_pension_start_date",
            )}
            render={({ field }) => (
              <DatePicker className="w-full" {...field} disabled={disabled} />
            )}
          />
          <QuestionnaireExpiredDateWarningField
            form={form}
            name="disability_pension_end_date"
            label={t(
              "questionnaires.form.conditionalFields.disability_pension_end_date",
            )}
            render={({ field }) => (
              <DatePicker className="w-full" {...field} disabled={disabled} />
            )}
          />
          <Field
            name="disability_pension_office_name"
            label={t(
              "questionnaires.form.conditionalFields.disability_pension_office_name",
            )}
            render={({ field }) => (
              <Input
                {...field}
                disabled={disabled}
                placeholder={t(
                  "questionnaires.form.conditionalFields.disability_pension_office_name",
                )}
              />
            )}
          />
          <Field
            name="disability_pension_type"
            label={t(
              "questionnaires.form.conditionalFields.disability_pension_type",
            )}
            render={({ field }) => (
              <DisabilityPensionTypeSelect {...field} disabled={disabled} />
            )}
          />
          {disabilityPensionType === DISABILITY_PENSION_TYPE.OTHER && (
            <Field
              name="disability_pension_description"
              label={t(
                "questionnaires.statements.disability_pension_description",
              )}
              required={disabilityPensionType === "other"}
              render={({ field }) => (
                <Input
                  {...field}
                  disabled={disabled}
                  placeholder={t(
                    "questionnaires.statements.disability_pension_description",
                  )}
                />
              )}
            />
          )}
        </QuestionnaireConditionalSection>
      </div>

      <div className="space-y-4">
        <QuestionnaireCheckboxField
          name="has_valid_visa"
          label={t("questionnaires.form.statements.visa")}
          disabled={disabled}
        />

        <QuestionnaireConditionalSection
          condition={form.watch("has_valid_visa")}
        >
          <Field
            name="visa_number"
            label={t("questionnaires.form.conditionalFields.visa_number")}
            render={({ field }) => (
              <Input
                {...field}
                disabled={disabled}
                placeholder={t(
                  "questionnaires.form.conditionalFields.visa_number",
                )}
              />
            )}
          />
          <QuestionnaireExpiredDateWarningField
            form={form}
            name="visa_valid_until"
            label={t("questionnaires.form.conditionalFields.visa_valid_until")}
            render={({ field }) => (
              <DatePicker className="w-full" {...field} disabled={disabled} />
            )}
          />
          <QuestionnaireAttachmentField
            name="visa_attachments"
            label={t("questionnaires.form.conditionalFields.visa_attachments")}
            existingAttachments={attachments.visa_attachments}
            disabled={disabled}
          />
        </QuestionnaireConditionalSection>
      </div>

      <div className="space-y-4">
        <QuestionnaireCheckboxField
          name="has_polish_card"
          label={t("questionnaires.form.statements.polish_card")}
          disabled={disabled}
        />

        <QuestionnaireConditionalSection
          condition={form.watch("has_polish_card")}
        >
          <QuestionnaireExpiredDateWarningField
            form={form}
            name="polish_card_valid_until"
            label={t(
              "questionnaires.form.conditionalFields.polish_card_valid_until",
            )}
            render={({ field }) => (
              <DatePicker className="w-full" {...field} disabled={disabled} />
            )}
          />
          <QuestionnaireAttachmentField
            name="polish_card_attachments"
            label={t(
              "questionnaires.form.conditionalFields.polish_card_attachments",
            )}
            existingAttachments={attachments.polish_card_attachments}
            disabled={disabled}
          />
        </QuestionnaireConditionalSection>
      </div>

      <div className="space-y-4">
        <QuestionnaireCheckboxField
          name="has_residence_card"
          label={t("questionnaires.form.statements.residence_card")}
          disabled={disabled}
        />

        <QuestionnaireConditionalSection
          condition={form.watch("has_residence_card")}
        >
          <QuestionnaireExpiredDateWarningField
            form={form}
            name="residence_card_valid_until"
            label={t(
              "questionnaires.form.conditionalFields.residence_card_valid_until",
            )}
            render={({ field }) => (
              <DatePicker className="w-full" {...field} disabled={disabled} />
            )}
          />
          <QuestionnaireAttachmentField
            name="residence_card_attachments"
            label={t(
              "questionnaires.form.conditionalFields.residence_card_attachments",
            )}
            existingAttachments={attachments.residence_card_attachments}
            disabled={disabled}
          />
        </QuestionnaireConditionalSection>
      </div>

      {!hasWatchedInstructionalVideos && (
        <div className="space-y-4">
          <div className="aspect-video w-full max-w-2xl mx-auto">
            <iframe
              src={QUESTIONNAIRE_INSTRUCTIONAL_VIDEO_URL}
              title={t("questionnaires.form.instructionalVideo.title")}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full rounded-lg"
            ></iframe>
          </div>

          <QuestionnaireCheckboxField
            name="watched_instructional_videos"
            label={t("questionnaires.form.statements.instructional_video")}
            disabled={disabled}
            required={isInstructionalVideosRequired(
              birthDate,
              isStudent,
              isEmployed,
            )}
          />
        </div>
      )}
    </QuestionnaireFormSection>
  );
}
